import { useState } from 'react';
import type { FormEvent } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import { saveSource } from './api';
import type { IntegrationSourceInfo, SaveSourceBody } from './api';
import { healthTone } from './parts';
import { useMonitorParams } from './useMonitorParams';
import styles from './MonitorPage.module.css';

/**
 * One source's settings, in a dialog over the list.
 *
 * Over it rather than beside it, like the dictionaries' forms: a column beside the
 * table squeezed every row while a source was open and reflowed them again on close.
 *
 * **The body is applied whole**, so every save sends every field the form shows —
 * an omitted `pollIntervalSeconds` cannot mean "keep it", because `null` is itself a
 * meaningful value ("do not judge staleness"). There is no delete and no create:
 * the first save creates the registry row, and "back to stock" is submitting the
 * defaults.
 *
 * The archive re-read is a command on the same PUT, offered only for
 * `KAZ_NAVIGATION` — the other sources accept the flag and ignore it, and a button
 * that does nothing is worse than no button.
 */

interface SourcePanelProps {
  source: IntegrationSourceInfo;
  onSaved: (source: IntegrationSourceInfo) => void;
  onClose: () => void;
}

interface Draft {
  displayName: string;
  enabled: boolean;
  /** As typed. Empty means null — a push feed with no staleness judgement. */
  interval: string;
  trust: string;
}

const draftFrom = (source: IntegrationSourceInfo): Draft => ({
  displayName: source.displayName ?? '',
  enabled: source.enabled,
  interval: source.pollIntervalSeconds === null ? '' : String(source.pollIntervalSeconds),
  trust: String(source.trustRank),
});

export const SourcePanel = ({ source, onSaved, onClose }: SourcePanelProps) => {
  const { t, formatDateTime } = useTranslation();
  const { update } = useMonitorParams();

  // Keyed by sourceCode in the caller, so another row remounts this — no sync effect.
  const [draft, setDraft] = useState<Draft>(() => draftFrom(source));
  const [fieldErrors, setFieldErrors] = useState<{ interval?: string; trust?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Against the source as the list holds it, which a save replaces with what was stored.
  const isDirty = JSON.stringify(draft) !== JSON.stringify(draftFrom(source));

  const set =<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    setFormError(null);
    setSaved(false);
  };

  /** One write path for the save button and the two replay commands. */
  const run = async (replayRequested?: boolean) => {
    const interval = draft.interval.trim();
    const trust = draft.trust.trim();

    const problems: { interval?: string; trust?: string } = {};
    if (interval !== '' && !/^\d+$/.test(interval)) {
      problems.interval = t('monitor.panel.intervalInvalid');
    }
    const trustValue = Number(trust);
    if (!/^\d+$/.test(trust) || trustValue < 0 || trustValue > 100) {
      problems.trust = t('monitor.panel.trustInvalid');
    }

    if (problems.interval || problems.trust) {
      setFieldErrors(problems);
      return;
    }

    const body: SaveSourceBody = {
      displayName: draft.displayName.trim() === '' ? null : draft.displayName.trim(),
      enabled: draft.enabled,
      pollIntervalSeconds: interval === '' ? null : Number(interval),
      trustRank: trustValue,
      // A plain save leaves a running re-read alone; only the two buttons set this.
      ...(replayRequested === undefined ? {} : { replayRequested }),
    };

    setFieldErrors({});
    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await saveSource(source.sourceCode, body);
      onSaved(result);
      // Rebased on what came back, so the form shows what was actually stored.
      setDraft(draftFrom(result));
      setSaved(true);
    } catch (caught) {
      setFormError(messageFor(caught, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void run();
  };

  /**
   * The sources screen's loop: a red light, and the reason one click away.
   *
   * Switching view, setting both filters and closing the panel is ONE call — two
   * would not compose, because each builds its URL from the current render's params
   * rather than from the other's result. See `update` in useMonitorParams.
   *
   */
  const showErrors = () =>
    update({ view: 'events', source: source.sourceCode, type: 'ERROR', panel: null });

  return (
    <Modal
      label={`${t('monitor.panel.title')} — ${source.sourceCode}`}
      onClose={onClose}
      dismissible={!isDirty && !isSubmitting}
    >
      <header className={styles.panelHeader}>
        <div className={styles.identity}>
          <span className={styles.panelCode}>{source.sourceCode}</span>
          <div className={styles.badges}>
            <Badge tone={source.enabled ? healthTone[source.health] : 'neutral'}>
              {source.enabled ? t(`monitor.health.${source.health}`) : t('monitor.sources.off')}
            </Badge>
            {source.replayPending && <Badge tone="info">{t('monitor.sources.replayingStart')}</Badge>}
          </div>
        </div>

        <button type="button" className={styles.close} onClick={onClose} aria-label={t('common.close')}>
          ×
        </button>
      </header>

      <div className={styles.panelBody}>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {formError && (
            <p className={styles.error} role="alert">
              {formError}
            </p>
          )}
          {saved && (
            <p className={styles.flash} role="status">
              {t('monitor.panel.saved')}
            </p>
          )}

          <Field
            label={t('monitor.panel.displayName')}
            value={draft.displayName}
            onChange={(event) => set('displayName', event.target.value)}
            autoComplete="off"
            disabled={isSubmitting}
          />

          <div className={styles.field}>
            <span className={styles.label}>{t('monitor.panel.enabled')}</span>
            <Select
              size="md"
              options={[
                { value: 'true', label: t('monitor.panel.on') },
                { value: 'false', label: t('monitor.sources.off') },
              ]}
              value={draft.enabled ? 'true' : 'false'}
              onChange={(event) => set('enabled', event.target.value === 'true')}
              disabled={isSubmitting}
              aria-label={t('monitor.panel.enabled')}
            />
          </div>

          <Field
            label={t('monitor.panel.interval')}
            value={draft.interval}
            onChange={(event) => set('interval', event.target.value)}
            {...(fieldErrors.interval ? { error: fieldErrors.interval } : {})}
            inputMode="numeric"
            autoComplete="off"
            disabled={isSubmitting}
            trailing={
              source.defaultPollIntervalSeconds !== null &&
              draft.interval !== String(source.defaultPollIntervalSeconds) ? (
                <button
                  type="button"
                  className={styles.inlineAction}
                  onClick={() => set('interval', String(source.defaultPollIntervalSeconds))}
                  disabled={isSubmitting}
                >
                  {t('monitor.panel.resetDefault', { value: source.defaultPollIntervalSeconds })}
                </button>
              ) : undefined
            }
          />

          <Field
            label={t('monitor.panel.trust')}
            value={draft.trust}
            onChange={(event) => set('trust', event.target.value)}
            {...(fieldErrors.trust ? { error: fieldErrors.trust } : {})}
            inputMode="numeric"
            autoComplete="off"
            disabled={isSubmitting}
          />

          {source.configured && source.updated && (
            <p className={styles.stampLine}>
              {t('monitor.panel.updatedBy', {
                name: source.updatedBy ?? '—',
                date: formatDateTime(source.updated),
              })}
            </p>
          )}

          <div className={styles.actions}>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? t('monitor.panel.saving') : t('common.save')}
            </Button>
            <Button onClick={showErrors} disabled={isSubmitting}>
              {t('monitor.panel.showErrors')}
            </Button>
          </div>
        </form>

        {source.sourceCode === 'KAZ_NAVIGATION' && (
          <section className={styles.replay}>
            <h3 className={styles.replayTitle}>{t('monitor.panel.replayTitle')}</h3>
            <p className={styles.hint}>{t('monitor.panel.replayHint')}</p>

            {source.replayPending ? (
              <>
                <p className={styles.replayState}>
                  {source.replayProgress
                    ? t('monitor.panel.replayRunning', { progress: source.replayProgress })
                    : t('monitor.panel.replayRunningStart')}
                </p>
                <Button size="sm" onClick={() => void run(false)} disabled={isSubmitting}>
                  {t('monitor.panel.replayCancel')}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => void run(true)} disabled={isSubmitting}>
                {t('monitor.panel.replayStart')}
              </Button>
            )}
          </section>
        )}
      </div>
    </Modal>
  );
};

const messageFor = (caught: unknown, t: (key: Key) => string): string => {
  const status = caught instanceof ApiError ? caught.status : 0;

  if (status === 403) return t('monitor.errorForbidden');
  return status === 0 ? t('monitor.panel.networkError') : t('monitor.panel.serverError');
};
